import { AdsTargetingProvider } from '@folklore/ads';
import { useWindowSize } from '@folklore/hooks';
import { useTracking } from '@folklore/tracking';
import { getComponentFromName } from '@folklore/utils';
import { useQueryClient } from '@tanstack/react-query';
import classNames from 'classnames';
import throttle from 'lodash-es/throttle';
import queryString from 'query-string';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation, useSearch } from 'wouter';

import { useAccess, useAccessPromotion } from '../../hooks/useAccess';
import useCollection from '../../hooks/useCollection';
import useDocument, { useDocumentPreview, useDocumentProgress } from '../../hooks/useDocument';
import useDocumentIsTrailer from '../../hooks/useDocumentIsTrailer';
import useElementSize from '../../hooks/useElementSize';
import useScrollEnter from '../../hooks/useScrollEnter';
import { useDocumentSurtitle } from '../../hooks/useSurtitle';
import { useDocumentPageViewTracking, useTrackOnClickCard } from '../../hooks/useTracking';
import { useDocumentTypeFromPath, useDocumentUrlGenerator } from '../../hooks/useUrlGenerator';
import useVisualViewport from '../../hooks/useVisualViewport';
import { useWindowScrolledPast } from '../../hooks/useWindowScrollValue';
import { createAuthorRow, createCollectionRow, createRelatedDocumentsRow } from '../../lib/blocks';

import { DocumentAdsTargetingProvider } from '../../contexts/AdsTargeting';
import { hasEpisodeLayout } from '../../contexts/AudioPlayerContext';
import { useHasPermission, useHasJoinedGroup } from '../../contexts/AuthContext';
import { useHasModals } from '../../contexts/PopupsContext';
import { useRequestBase } from '../../contexts/RequestContext';
import { useDefaultBrand } from '../../contexts/SiteContext';
import { useVisitorId } from '../../contexts/VisitorContext';
import Blocks from '../blocks/Blocks';
import ProShareButton from '../buttons/ProShareButton';
import SerieCollection from '../collections/SerieCollection';
import VideoMetadata from '../documents/VideoMetadata';
import * as Documents from '../documents/index';
import AdminMenu from '../menus/AdminMenu';
import LazyComponent from '../partials/LazyComponent';
// import DocumentsList from '../lists/DocumentsList';
import LoadingBar from '../partials/LoadingBar';
import PromptNotification from '../popups/PromptNotification';
import ShareModal from '../popups/ShareModal';
import ListTitle from '../typography/ListTitle';
import NotFoundPage from './NotFoundPage';

import styles from '../../styles/pages/document-page.module.css';

export interface DocumentPageProps {
    slug?: string | null;
    previewToken?: string | null;
    paused?: boolean;
    transitioning?: boolean;
    current?: boolean;
    entered?: boolean;
    entering?: boolean;
    leaving?: boolean;
    disabled?: boolean;
    className?: string | null;
    contentClassName?: string | null;
    onEnter?: (() => void) | null;
    onLeave?: (() => void) | null;
}

const DocumentPage = ({
    slug = null,
    previewToken = null,
    paused = false,
    transitioning = false,
    current = false,
    entered = false,
    entering = false,
    leaving = false,
    disabled = false,
    className = null,
    contentClassName = null,
    onEnter = null,
    onLeave = null,
}: DocumentPageProps) => {
    const hasModals = useHasModals();
    const hasAdminMenu = useHasPermission('admin_menu');
    const isPro = useHasJoinedGroup('pro');
    const hasAttributionAccess = useHasPermission('attribution');
    const hasShareTool = isPro || hasAttributionAccess;

    const {
        document: previewLoadedDocument = null,
        notFound: previewNotFound = false,
        isFetching: previewIsFetching = false,
    } = useDocumentPreview(previewToken, {
        keepPreviousData: false,
        enabled: !disabled && previewToken !== null,
    });
    const {
        document: normalLoadedDocument = null,
        notFound: normalNotFound = false,
        isFetching: normalIsFetching = false,
    } = useDocument(slug, {
        keepPreviousData: false,
        enabled: !disabled && previewToken === null,
    });
    const loadedDocument = previewToken !== null ? previewLoadedDocument : normalLoadedDocument;
    const notFound = previewToken !== null ? previewNotFound : normalNotFound;
    const isFetching = previewToken !== null ? previewIsFetching : normalIsFetching;

    const [promptedDocument, setPromptedDocument] = useState<FullDocument | null>(null);
    // const currentDocument = promptedDocument || loadedDocument;
    const currentDocument = promptedDocument || loadedDocument;

    // Current document
    const id = currentDocument?.id || null;
    const type = currentDocument?.type || null;
    const title = currentDocument?.title || null;
    const documentTheme = currentDocument?.theme || null;
    const metadata = currentDocument?.metadata || null;
    const collections = metadata?.collections || [];
    const credits = metadata?.credits || null;
    const authors = metadata?.authors || null;
    const brands = metadata?.brands || null;
    const withoutFooter = documentTheme?.withoutFooter || false;
    const withoutMetadata = documentTheme?.withoutMetadata || false;
    const withoutAds = documentTheme?.withoutAds || false;
    const fullscreen = documentTheme?.fullscreen || false;

    const [location] = useLocation();
    const search = useSearch();
    const { ai = null, autoplay = false } = queryString.parse(search);
    const showAi = ai !== null;
    const autoPlay = autoplay !== false;
    const url = useDocumentUrlGenerator();
    const baseUri = useRequestBase();
    const visitorId = useVisitorId();
    const defaultBrand = useDefaultBrand();
    const creditAuthor = credits?.[0]?.author || null;
    // const [{ author: creditAuthor = null } = {}] = credits || [];
    const author = authors?.[0] || creditAuthor;
    // const [author = creditAuthor] = authors || [];
    const brand = brands?.[0] || null;
    const collection = collections?.[0] || null;
    const collectionSlug = collection?.slug || null;
    const serie = collections.reduce((acc, coll) => {
        const { serie = null, type: collectionType = null } = coll || {};
        if (collectionType.indexOf('serie') !== -1) {
            return coll || acc;
        }
        return serie || acc;
    }, null);
    const videoCollectionSlug =
        serie?.slug || (type === 'video' || type === 'podcast' ? collectionSlug : null) || null;
    const { collection: videoCollection = null } = useCollection(videoCollectionSlug, {
        enabled: videoCollectionSlug !== null && current,
    });
    const { theme: collectionTheme = null } = videoCollection || {};
    const { handle: brandHandle = null } = brand || defaultBrand || {};
    const authorSlug = author?.slug || null;
    const authorIsPerson = author?.isPerson || false;
    const authorId = author?.id || null;
    const locationType = useDocumentTypeFromPath(location);
    const finalDocumentType = type || locationType || null;

    const isVideo = finalDocumentType === 'video';
    // The playlist of a podcast episode replaces the serie under the metadata
    const isPodcastEpisode = hasEpisodeLayout(currentDocument as PodcastDocument | null);
    const isMicromag = finalDocumentType === 'micromag';
    const isArticle =
        finalDocumentType === 'article' ||
        finalDocumentType === 'contest' ||
        finalDocumentType === 'article_legacy' ||
        finalDocumentType === 'newsletter' ||
        finalDocumentType === 'survey';

    const isTrailer = useDocumentIsTrailer(currentDocument, location);
    const documentUrl =
        slug !== null && type !== null
            ? `${baseUri}${url({
                  type,
                  slug,
              })}`
            : null;
    const DocumentComponent = getComponentFromName(Documents, finalDocumentType, null);

    const { ref: documentContainerRef, height: documentHeight = 0 } = useElementSize();
    const { ref: metadataContentRef, height: metadataContentHeight = 0 } = useElementSize();

    const placeholder = currentDocument === null;

    const surtitle = useDocumentSurtitle(currentDocument);

    const onClickRelatedCard = useTrackOnClickCard('click_related');

    const currentAdsDisabled = !current || currentDocument === null || withoutAds;
    const [adsDisabled, setAdsDisabled] = useState(currentAdsDisabled);
    if (!transitioning && currentAdsDisabled !== adsDisabled) {
        setAdsDisabled(currentAdsDisabled);
    }

    // The current page follows the scroll; a page that is no longer current keeps its footer
    // as it was, so it does not change while it leaves.
    const scrolledPastTop = useWindowScrolledPast(10, { disabled: !current });
    const currentFooterContentVisible = ((entering || entered) && !leaving) || scrolledPastTop;
    const [footerContentVisible, setFooterContentVisible] = useState(currentFooterContentVisible);
    if (current && currentFooterContentVisible !== footerContentVisible) {
        setFooterContentVisible(currentFooterContentVisible);
    }
    // const footerContentVisible = true;
    const isFullscreen = fullscreen || (isMicromag && !isTrailer);
    const footerDisabled = !footerContentVisible || (isFullscreen && entered);
    const footerAdsDisabled = !footerContentVisible || (isFullscreen && entered) || adsDisabled;

    const withMetadataContent = !isArticle && !withoutMetadata;
    const withCollectionRow = collectionSlug !== null && videoCollectionSlug === null;
    const withAuthorRow = authorSlug !== null && authorIsPerson;

    const footerBlocks =
        id !== null
            ? [
                  withCollectionRow
                      ? {
                            type: 'ad',
                            slot: 'fullwidth',
                            disabled: footerAdsDisabled,
                            loading: 'lazy',
                        }
                      : null,
                  withCollectionRow
                      ? createCollectionRow(collectionSlug, {
                            query: {
                                exclude_id: id,
                            },
                            disabled: footerDisabled,
                            cardSize: null,
                            cardTheme: 'rounded',
                            cardWidth: 200,
                            presentation: 'grid',
                            clickRef: 'document-footer-collection',
                            count: 12,
                            summaryPosition: null,
                            loading: 'lazy',
                            //   withoutSummaryImage: true,
                            //   withBackground: true,
                            withBorderRadius: false,
                            cardWith: ['brand-icon'],
                            cardWithout: ['serie', 'collection', 'time', 'author'],
                            className: classNames([styles.row, styles.flush]),
                            innerClassName: styles.inner,
                            summaryClassName: styles.summary,
                            summaryButtonClassName: styles.summaryButton,
                        })
                      : null,
                  withAuthorRow
                      ? createAuthorRow(authorSlug, {
                            query: {
                                exclude_id: id,
                            },
                            disabled: footerDisabled,
                            cardSize: null,
                            cardTheme: 'rounded',
                            cardWith: ['brand-icon'],
                            cardWithout: ['author', 'time'],
                            cardWidth: 250,
                            clickRef: 'document-footer-author',
                            count: 6,
                            presentation: 'grid',
                            summaryPosition: null,
                            background: null,
                            loading: 'lazy',
                            className: styles.row,
                            innerClassName: styles.inner,
                            summaryClassName: styles.summary,
                            summaryButtonClassName: styles.summaryButton,
                        })
                      : null,
                  {
                      type: 'ad',
                      slot: 'fullwidth',
                      disabled: footerAdsDisabled,
                      loading: 'lazy',
                  },
                  visitorId !== null
                      ? createRelatedDocumentsRow(
                            id,
                            authorIsPerson && authorId !== null
                                ? {
                                      referrer: documentUrl,
                                      visitor_id: visitorId,
                                      exclude_author: authorId,
                                  }
                                : {
                                      referrer: documentUrl,
                                      visitor_id: visitorId,
                                  },
                            {
                                header: (
                                    <ListTitle className={styles.title}>
                                        <FormattedMessage
                                            defaultMessage="Plus de contenus"
                                            description="Section title"
                                        />
                                    </ListTitle>
                                ),
                                disabled: footerDisabled,
                                presentation: 'grid',
                                cardSize: null,
                                cardTheme: 'rounded',
                                cardWidth: 250,
                                cardWith: ['brand-icon'],
                                clickRef: 'document-footer-related',
                                onClickCard: onClickRelatedCard,
                                count: 12,
                                loading: 'lazy',
                                summaryPosition: null,
                                background: null,
                                className: styles.row,
                                innerClassName: styles.inner,
                            },
                        )
                      : null,
                  {
                      type: 'ad',
                      slot: 'fullwidth',
                      disabled: footerAdsDisabled,
                      loading: 'lazy',
                  },
              ].filter((it) => it !== null)
            : [];

    const scrolledPastDocument = useWindowScrolledPast(documentHeight, { disabled: !current });
    useScrollEnter({
        leave: current && scrolledPastDocument && isArticle && !isFullscreen,
        disabled: !current || (current && isFullscreen && !isArticle) || transitioning,
        leaveDisabled: !current || (current && isFullscreen),
        leaveThreshold:
            documentHeight > 0 && metadataContentHeight > 0
                ? documentHeight + metadataContentHeight
                : null,
        onEnter,
        onLeave,
    });

    // useEffect(() => {
    //     if (current && isFullscreen && onEnter !== null && !entered) {
    //         onEnter();
    //     }
    // }, [current, isFullscreen, onEnter]);

    const tracking = useTracking();
    const trackingDisabled = !current || currentDocument === null || previewToken !== null;
    const [started, setStarted] = useState(false);
    useDocumentPageViewTracking(!trackingDisabled, currentDocument);

    const queryClient = useQueryClient();

    const { refetch: refetchProgress } = useDocumentProgress(!placeholder ? id : null);
    const throttledRefetchProgress = throttle(refetchProgress, 2000);
    const onDocumentProgress = (progress) => {
        if (trackingDisabled) {
            return;
        }
        queryClient.invalidateQueries({ queryKey: ['document_progress', id] });
        tracking.trackEvent('Document', 'progress', title, null, {
            documentProgress: progress * 100,
            document: currentDocument,
        });
        if (!started) {
            tracking.trackEvent('Document', 'start', title, null, {
                document: currentDocument,
            });
            setStarted(true);
        }
        throttledRefetchProgress();
    };

    const onDocumentComplete = () => {
        if (trackingDisabled) {
            return;
        }
        tracking.trackEvent('Document', 'complete', title, null, {
            documentProgress: 100,
            document: currentDocument,
        });
    };

    const { pageTop = null, height: visualHeight } = useVisualViewport({ disabled: !current });
    const { height: windowHeight = 0 } = useWindowSize();

    const currentIdlePaused = !current;
    const [idlePaused, setIdlePaused] = useState(currentIdlePaused);
    if (idlePaused !== currentIdlePaused) {
        setIdlePaused(currentIdlePaused);
    }

    const footerAdsTargeting = {
        disabled: footerAdsDisabled,
    };

    const onPromptResponse = (response) => {
        setPromptedDocument(response);
    };

    const [hasEntered, setHasEntered] = useState(false);
    const onDocumentEnter = () => {
        setHasEntered(true);
        if (onEnter !== null) {
            onEnter();
        }
    };

    const finalAccess = useAccess(currentDocument);
    useAccessPromotion(finalAccess, {
        type: finalDocumentType,
        disabled: id === null,
        delay: isVideo ? 5000 : 0,
    });

    const [shareOpened, setShareOpened] = useState(false);
    const onClickProShare = () => {
        setShareOpened(true);
    };
    const onShareModalClosed = () => {
        setShareOpened(false);
    };

    if (notFound) {
        return <NotFoundPage current={current} />;
    }

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.transitioning]: transitioning,
                    [styles.isLoading]: isFetching,
                    [styles.current]: current,
                    [styles.entered]: (entering || entered) && !leaving,
                    [styles.leaved]: (!entering && !entered) || leaving,
                    [styles.withoutFooter]: withoutFooter,
                    [styles.withoutMetadata]: withoutMetadata,
                    [styles.withoutMinHeight]: isTrailer && isVideo,
                    [styles.footerContentVisible]:
                        footerContentVisible || hasEntered || (isTrailer && isVideo) || true,
                    [styles.isFullscreen]: isFullscreen,
                },
                className,
            ])}
            data-brand={brandHandle}
            style={
                pageTop !== null &&
                pageTop > 0 &&
                isFullscreen &&
                !isArticle &&
                entered &&
                current &&
                visualHeight === windowHeight &&
                Math.round(documentHeight) === Math.round(windowHeight) && // is Micromag
                pageTop === window.scrollY
                    ? {
                          transform: `translateY(${pageTop}px)`,
                      }
                    : null
            }
        >
            {hasAdminMenu ? (
                <AdminMenu className={styles.toolbar} buttonClassName={styles.toolbarButton} />
            ) : hasShareTool ? (
                <ProShareButton
                    className={classNames([styles.toolbar, styles.toolbarButton])}
                    onClick={onClickProShare}
                />
            ) : null}
            {currentDocument !== null && current && showAi ? (
                <PromptNotification
                    agent="article_editor"
                    instructionsExtra={JSON.stringify(currentDocument)}
                    isJson
                    onResponse={onPromptResponse}
                />
            ) : null}
            <DocumentAdsTargetingProvider document={currentDocument} disabled={adsDisabled}>
                <LoadingBar
                    loading={currentDocument === null}
                    loaded={currentDocument !== null}
                    className={styles.loadingBar}
                />
                {DocumentComponent !== null ? (
                    <div>
                        <div className={styles.documentContainer} ref={documentContainerRef}>
                            <LazyComponent
                                component={DocumentComponent}
                                fallback={
                                    <div
                                        className={classNames([
                                            styles.document,
                                            styles[finalDocumentType],
                                            {
                                                [styles.article]: isArticle,
                                                [contentClassName]:
                                                    contentClassName !== null && !isTrailer,
                                            },
                                        ])}
                                    />
                                }
                                {...currentDocument}
                                current={current}
                                entered={entered}
                                entering={entering}
                                leaving={leaving}
                                transitioning={transitioning}
                                withHeaderAd={!isFullscreen}
                                onEnter={onDocumentEnter}
                                onLeave={onLeave}
                                paused={!current || hasModals || paused || idlePaused}
                                autoPlay={current && autoPlay}
                                placeholder={placeholder}
                                isPreview={previewToken !== null}
                                onComplete={onDocumentComplete}
                                onProgress={onDocumentProgress}
                                className={classNames([
                                    styles.document,
                                    styles[finalDocumentType],
                                    // {
                                    //     [styles.withMetadata]:
                                    //         metadataHeight > 60 && !isArticle,
                                    // },
                                    {
                                        [styles.article]: isArticle,
                                        [contentClassName]: contentClassName !== null && !isTrailer,
                                    },
                                ])}
                            />
                        </div>
                        {withMetadataContent || !withoutFooter ? (
                            <div
                                className={classNames([
                                    styles.footer,
                                    {
                                        [styles.hasContent]: !isArticle,
                                    },
                                ])}
                                data-theme="dark"
                            >
                                <AdsTargetingProvider targeting={footerAdsTargeting}>
                                    {withMetadataContent ? (
                                        <div
                                            className={styles.content}
                                            ref={metadataContentRef}
                                            data-theme="normal"
                                        >
                                            <VideoMetadata
                                                {...currentDocument}
                                                surtitle={surtitle}
                                                theme={documentTheme || collectionTheme}
                                            />
                                            {videoCollection !== null && !isPodcastEpisode ? (
                                                <SerieCollection
                                                    {...videoCollection}
                                                    current={current}
                                                    // currentDocument={currentDocument}
                                                    withoutHeaderImage
                                                />
                                            ) : null}
                                        </div>
                                    ) : null}

                                    {!withoutFooter ? (
                                        <Blocks
                                            blocks={footerBlocks}
                                            blockClassName={styles.block}
                                        />
                                    ) : null}
                                </AdsTargetingProvider>
                            </div>
                        ) : null}
                    </div>
                ) : (
                    <div className={styles.document} />
                )}
            </DocumentAdsTargetingProvider>
            {shareOpened ? (
                <ShareModal onClosed={onShareModalClosed} document={currentDocument} />
            ) : null}
        </div>
    );
};

export default DocumentPage;
